import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { 
  GetAllReservationsByIdApi,
  GetAllReservationsApi,
  GetAllReservationsByUserApi,
  GetAllReservationsByGiftApi,
  GetAllReservationsByPaywayApi
} from "../Apis/Reservations/Reservations";
import { GetAllReservationsByDateApi } from "../Apis/Autocompleteapi";



export const fetchReservationsByPayWay = createAsyncThunk(
  'reservations/fetchByPayWay',
  async ({ page = 1, limit = 10, payWay, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await GetAllReservationsByPaywayApi({
        page,
        limit,
        payWay,
        filters
      });

      return {
        data: response.data,
        pagination: {
          page: response.page,
          total: response.total,
          totalPages: response.totalPages,
          limit: response.limit || limit,
        },
        payWay 
      };
    } catch (error) {
      return rejectWithValue({
        status: error.status || 500,
        message: error.message || "Failed to fetch reservations by pay way",
      });
    }
  }
);


export const fetchReservationsByGift = createAsyncThunk(
  'reservations/fetchByGift',
  async ({ isAGift, page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await GetAllReservationsByGiftApi({ 
        page,
        limit,
        isAGift,
        filters
      });

      // Debug logging to verify the response
      console.log('Redux Thunk Response:', {
        dataLength: response.data.length,
        page: response.page,
        total: response.total
      });

      return {
        data: response.data,
        pagination: {
          page: response.page,
          total: response.total,
          totalPages: response.totalPages,
          limit: response.limit,
        }
      };
    } catch (error) {
      return rejectWithValue({
        status: error.status || 500,
        message: error.message || "Failed to fetch gift reservations",
      });
    }
  }
);
  


export const fetchdateOfReservationAutocomplete = createAsyncThunk(
  "reservations/fetchByDate",
  async ({ page, limit, dateOfReservation ,isPaid=true}, { rejectWithValue }) => {
    try {
      console.log("Date being passed:", dateOfReservation);
      const response = await GetAllReservationsByDateApi({ page, limit, dateOfReservation,isPaid });
      
      // التأكد من أن البيانات هي مصفوفة
      let dataArray = [];
      if (Array.isArray(response.data)) {
        dataArray = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // في حالة كانت البيانات كائن، نحاول استخراج المصفوفة
        if (Array.isArray(response.data.data)) {
          dataArray = response.data.data;
        } else if (Array.isArray(response.data.reservations)) {
          dataArray = response.data.reservations;
        }
      }

      // توحيد وجود معرف لكل حجز (مهم للتعديلات)
      const normalizedData = dataArray.map((item) => {
        const fallbackId = item.reservationId || item.reservationNumber || item.id;
        return {
          ...item,
          _id: item._id || fallbackId,
          id: item._id || fallbackId,
        };
      });
      
      return {
        data: normalizedData,
        status: response.status,
        total: response.total || normalizedData.length,
        page: response.page || page,
        totalPages: response.totalPages || Math.ceil((response.total || normalizedData.length) / (limit || 10))
      };
    } catch (error) {
      return rejectWithValue({
        status: error.status || 500,
        message: error.message || "Failed to fetch reservations by date",
      });
    }
  }
);


export const fetchReservations = createAsyncThunk(
  "reservations/fetchAll",
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await GetAllReservationsApi({ page, limit, filters });
      
      return {
        data: response.data,
        pagination: {
          page: response.page,
          total: response.total,
          totalPages: response.totalPages,
          limit: response.limit || limit,
        }
      };
    } catch (error) {
      return rejectWithValue({
        status: error.status || 500,
        message: error.message || "Failed to fetch reservations",
      });
    }
  }
);


export const fetchGetAllReservationsByIdApi = createAsyncThunk(
  'reservations/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await GetAllReservationsByIdApi(id);
      return response;
    } catch (error) {
      return rejectWithValue({
        status: error.status || 500,
        message: error.message || "Failed to fetch reservation by ID",
      });
    }
  }
);


export const fetchReservationsByUser = createAsyncThunk(
  'reservations/fetchByUser',
  async ({ userId, page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await GetAllReservationsByUserApi({ 
        userId,
        page, 
        limit,
        filters
      });
      return {
        data: response.data,
        pagination: {
          page: response.page,
          total: response.total,
          totalPages: response.totalPages,
          limit: response.limit || limit,
        },
        userId 
      };
    } catch (error) {
      return rejectWithValue({
        status: error.status || 500,
        message: error.message || "Failed to fetch user reservations",
      });
    }
  }
);

const initialState = {
  data: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    totalPages: 0,
    pageSize: 10,
  },
  currentUserId: null 
};

const ReservationsSlice = createSlice({
  name: 'Reservations',
  initialState,
  reducers: {
    clearReservations: (state) => {
      state.data = [];
      state.pagination = initialState.pagination;
      state.currentUserId = null;
    },
    updateReservationInStore: (state, action) => {
      const { id, reservationNumber, updates = {} } = action.payload || {};
      if (!id && !reservationNumber) return;

      const matchById = (reservation) => {
        const currentId = reservation?._id || reservation?.id;
        return currentId && id && currentId.toString() === id.toString();
      };

      const matchByNumber = (reservation) => {
        if (!reservationNumber) return false;
        return (
          reservation?.reservationNumber?.toString() === reservationNumber.toString()
        );
      };

      state.data = state.data.map((reservation) => {
        if (matchById(reservation) || matchByNumber(reservation)) {
          return {
            ...reservation,
            ...updates,
            _id: reservation._id || updates._id || id,
            id: reservation.id || updates.id || id,
          };
        }
        return reservation;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGetAllReservationsByIdApi.pending, (state) => {
        state.isLoading = true;
        state.data = [];
      })
      .addCase(fetchGetAllReservationsByIdApi.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(fetchGetAllReservationsByIdApi.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchdateOfReservationAutocomplete.pending, (state) => {
        state.isLoading = true;
        state.data = [];
      })
      .addCase(fetchdateOfReservationAutocomplete.fulfilled, (state, action) => {
        state.isLoading = false;
        // التأكد من أن البيانات مصفوفة قبل التخزين
        state.data = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.pagination = {
          page: action.payload.page || 1,
          pageSize: action.payload.limit || 10,
          total: action.payload.total || 0,
          totalPages: action.payload.totalPages || 0,
        };
      })
      .addCase(fetchdateOfReservationAutocomplete.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchReservations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.currentUserId = null;
      })
      .addCase(fetchReservations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchReservations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      .addCase(fetchReservationsByUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReservationsByUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.pagination = action.payload.pagination;
        state.currentUserId = action.payload.userId;
      })
      .addCase(fetchReservationsByUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.currentUserId = null;
      })
      .addCase(fetchReservationsByPayWay.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReservationsByPayWay.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchReservationsByPayWay.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchReservationsByGift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReservationsByGift.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchReservationsByGift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
  }
});

export default ReservationsSlice.reducer;
export const { clearReservations, updateReservationInStore } = ReservationsSlice.actions;